import {
  BadRequestException,
  ConflictException,
  Injectable,
  NotFoundException,
  Optional,
} from '@nestjs/common';
import { AuthUser, ThemeMode, UpdateProfileRequest, UserListResponse } from '@cart/shared-types';
import { AuditService } from '../audit/audit.service';
import { AuthRepository, StoredUser } from '../auth/auth.repository';
import { AuthService } from '../auth/auth.service';

@Injectable()
export class UsersService {
  constructor(
    private readonly authRepository: AuthRepository,
    private readonly authService: AuthService,
    @Optional()
    private readonly auditService?: AuditService
  ) {}

  async list(): Promise<UserListResponse> {
    const users = await this.authRepository.list();
    const items = await Promise.all(users.map((user) => this.toPublicUser(user)));

    return {
      items,
      total: items.length,
    };
  }

  async profile(userId: string): Promise<AuthUser> {
    const user = await this.authRepository.findById(userId);

    if (!user) {
      throw new NotFoundException('User not found');
    }

    const publicUser = await this.toPublicUser(user);

    await this.auditService?.logChange({
      schemaName: 'admin',
      tableName: 'users',
      recordId: user.id,
      operation: 'update',
      changedColumns: ['name', 'email'],
      newValue: publicUser,
      changedBy: userId,
      source: 'UsersService.updateProfile',
    });

    return publicUser;
  }

  async updateProfile(userId: string, input: UpdateProfileRequest): Promise<AuthUser> {
    this.validateProfile(input);

    const email = input.email.trim().toLowerCase();
    const existingEmailUser = await this.authRepository.findByEmail(email);

    if (existingEmailUser && existingEmailUser.id !== userId) {
      throw new ConflictException('Email is already registered');
    }

    const user = await this.authRepository.updateProfile(userId, {
      name: input.name.trim(),
      email,
    });

    if (!user) {
      throw new NotFoundException('User not found');
    }

    return this.toPublicUser(user);
  }

  async updateThemeMode(userId: string, themeMode: ThemeMode): Promise<AuthUser> {
    const user = await this.authRepository.updateThemeMode(userId, themeMode);

    if (!user) {
      throw new NotFoundException('User not found');
    }

    return this.toPublicUser(user);
  }

  private validateProfile(input: UpdateProfileRequest): void {
    if (!input.name?.trim()) {
      throw new BadRequestException('name is required');
    }

    if (!input.email?.trim() || !input.email.includes('@')) {
      throw new BadRequestException('valid email is required');
    }
  }

  private toPublicUser(user: StoredUser): Promise<AuthUser> {
    return this.authService.toAuthUser(user);
  }
}
